// src/pages/master/PlanManagement.jsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Typography,
  Divider,
} from "@mui/material";
import { styled } from "@mui/system";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import API from "../../api";
import ConfirmDialog from "../../components/ConfirmDialog";

const Container = styled(Box)({ padding: 16 });

export default function PlanManagement() {
  //
  // ─── PLANS STATE ─────────────────────────────────────────────────────────────
  //
  const [plans, setPlans] = useState([]);
  const [planOpen, setPlanOpen] = useState(false);
  const [planEditing, setPlanEditing] = useState(null);
  const [planForm, setPlanForm] = useState({
    name: "",
    minCost: "",
    maxCost: "",
    price: "",
  });
  const [planConfirm, setPlanConfirm] = useState({
    open: false,
    id: null,
    name: "",
  });

  //
  // ─── COMMISSIONS STATE ────────────────────────────────────────────────────────
  //
  const [comms, setComms] = useState([]);
  const [commOpen, setCommOpen] = useState(false);
  const [commEditing, setCommEditing] = useState(null);
  const [commForm, setCommForm] = useState({
    name: "",
    grade: "",
    percent: "",
  });
  const [commConfirm, setCommConfirm] = useState({
    open: false,
    id: null,
    name: "",
  });

  //
  // ─── SNACKBAR ────────────────────────────────────────────────────────────────
  //
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const show = (msg, sev = "error") =>
    setSnack({ open: true, message: msg, severity: sev });

  //
  // ─── LOAD BOTH LISTS ──────────────────────────────────────────────────────────
  //
  const load = async () => {
    try {
      const [plansRes, commsRes] = await Promise.all([
        API.get("/master/plans"),
        API.get("/master/commissions"),
      ]);
      setPlans(plansRes.data);
      setComms(commsRes.data);
    } catch {
      show("Failed to load data");
    }
  };

  useEffect(() => {
    load();
  }, []);

  //
  // ─── PLAN HANDLERS ────────────────────────────────────────────────────────────
  //
  const openAddPlan = () => {
    setPlanEditing(null);
    setPlanForm({ name: "", minCost: "", maxCost: "", price: "" });
    setPlanOpen(true);
  };
  const openEditPlan = (p) => {
    setPlanEditing(p);
    setPlanForm({
      name: p.name,
      minCost: p.minCost,
      maxCost: p.maxCost,
      price: p.price,
    });
    setPlanOpen(true);
  };
  const closePlanDialog = () => setPlanOpen(false);

  const handleSavePlan = async () => {
    try {
      if (planEditing) {
        await API.put(`/master/plans/${planEditing._id}`, planForm);
        show("Updated plan", "success");
      } else {
        await API.post("/master/plans", planForm);
        show("Created plan", "success");
      }
      load();
      closePlanDialog();
    } catch (err) {
      show(err.response?.data?.message || "Save failed");
    }
  };

  const askDeletePlan = (p) =>
    setPlanConfirm({ open: true, id: p._id, name: p.name });
  const cancelDeletePlan = () =>
    setPlanConfirm({ open: false, id: null, name: "" });
  const confirmDeletePlan = async () => {
    try {
      await API.delete(`/master/plans/${planConfirm.id}`);
      show("Deleted plan", "success");
      load();
    } catch {
      show("Delete failed");
    } finally {
      cancelDeletePlan();
    }
  };

  //
  // ─── COMMISSION HANDLERS ──────────────────────────────────────────────────────
  //
  const openAddComm = () => {
    setCommEditing(null);
    setCommForm({ name: "", grade: "", percent: "" });
    setCommOpen(true);
  };
  const openEditComm = (c) => {
    setCommEditing(c);
    setCommForm({ name: c.name, grade: c.grade, percent: c.percent });
    setCommOpen(true);
  };
  const closeCommDialog = () => setCommOpen(false);

  const handleSaveComm = async () => {
    try {
      if (commEditing) {
        await API.put(`/master/commissions/${commEditing._id}`, commForm);
        show("Updated commission", "success");
      } else {
        await API.post("/master/commissions", commForm);
        show("Created commission", "success");
      }
      load();
      closeCommDialog();
    } catch (err) {
      show(err.response?.data?.message || "Save failed");
    }
  };

  const askDeleteComm = (c) =>
    setCommConfirm({ open: true, id: c._id, name: c.name });
  const cancelDeleteComm = () =>
    setCommConfirm({ open: false, id: null, name: "" });
  const confirmDeleteComm = async () => {
    try {
      await API.delete(`/master/commissions/${commConfirm.id}`);
      show("Deleted commission", "success");
      load();
    } catch {
      show("Delete failed");
    } finally {
      cancelDeleteComm();
    }
  };

  return (
    <Container>
      {/* ─── PLANS ────────────────────────────────────────────────────────────── */}
      <Typography variant="h5" gutterBottom>
        Plan Management
      </Typography>
      <Box mb={2}>
        <Button variant="contained" onClick={openAddPlan}>
          Add Plan
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Min Cost</TableCell>
              <TableCell>Max Cost</TableCell>
              <TableCell>Price</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((p) => (
              <TableRow key={p._id}>
                <TableCell>{p.name}</TableCell>
                <TableCell>{p.minCost}</TableCell>
                <TableCell>{p.maxCost}</TableCell>
                <TableCell>{p.price}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openEditPlan(p)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => askDeletePlan(p)}
                  >
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm Delete Plan */}
      <ConfirmDialog
        open={planConfirm.open}
        title="Confirm Delete Plan"
        content={`Delete plan “${planConfirm.name}”?`}
        onCancel={cancelDeletePlan}
        onConfirm={confirmDeletePlan}
      />

      {/* Add/Edit Plan Dialog */}
      <Dialog open={planOpen} onClose={closePlanDialog}>
        <DialogTitle>{planEditing ? "Edit Plan" : "Add Plan"}</DialogTitle>
        <DialogContent>
          {[
            ["Name", "name"],
            ["Min Cost (lacs)", "minCost"],
            ["Max Cost (lacs)", "maxCost"],
            ["Price", "price"],
          ].map(([lab, key]) => (
            <TextField
              key={key}
              fullWidth
              size="small"
              margin="dense"
              label={lab}
              value={planForm[key]}
              onChange={(e) =>
                setPlanForm((f) => ({ ...f, [key]: e.target.value }))
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlanDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePlan}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Divider sx={{ my: 4 }} />

      {/* ─── COMMISSIONS ───────────────────────────────────────────────────────── */}
      <Typography variant="h5" gutterBottom>
        Commission Structure
      </Typography>
      <Box mb={2}>
        <Button variant="contained" onClick={openAddComm}>
          Add Commission
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Percent (%)</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {comms.map((c) => (
              <TableRow key={c._id}>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.grade}</TableCell>
                <TableCell>{c.percent}</TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => openEditComm(c)}>
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => askDeleteComm(c)}
                  >
                    <DeleteForeverIcon />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm Delete Commission */}
      <ConfirmDialog
        open={commConfirm.open}
        title="Confirm Delete Commission"
        content={`Delete commission “${commConfirm.name}”?`}
        onCancel={cancelDeleteComm}
        onConfirm={confirmDeleteComm}
      />

      {/* Add/Edit Commission Dialog */}
      <Dialog open={commOpen} onClose={closeCommDialog}>
        <DialogTitle>
          {commEditing ? "Edit Commission" : "Add Commission"}
        </DialogTitle>
        <DialogContent>
          {[
            ["Name", "name"],
            ["Grade", "grade"],
            ["Percent", "percent"],
          ].map(([lab, key]) => (
            <TextField
              key={key}
              fullWidth
              size="small"
              margin="dense"
              label={lab}
              value={commForm[key]}
              onChange={(e) =>
                setCommForm((f) => ({ ...f, [key]: e.target.value }))
              }
            />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCommDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveComm}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Container>
  );
}
